/**
 * FilterStrip Component (Organism)
 *
 * Global Filter Strip - persistent, horizontal UI component for filtering records.
 * Filters apply immediately when selections change.
 */

import { useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { Button } from '../../../../ui/button';
import type {
  FilterStripProps,
  FilterConfig,
  FilterWithLogic,
  ConductDimension,
  ActiveFilterChip,
} from '../types/filter.types';

import { DateRangeFilter } from './DateRangeFilter';
import { DimensionFilter } from './DimensionFilter';
import { ActiveFilterChips } from './ActiveFilterChips';

export function FilterStrip({
  filters,
  onFiltersChange,
  onReset,
  isLoading = false,
  className,
  tcfAvailable,
}: FilterStripProps) {

  // Check if any filters are active
  const hasActiveFilters =
    filters.dateRange?.preset ||
    filters.dateRange?.from ||
    filters.dateRange?.to ||
    filters.dimensions?.values?.length ||
    filters.search ||
    filters.conductScoreMin !== undefined ||
    filters.conductScoreMax !== undefined;

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (dateRange: FilterConfig['dateRange']) => {
      onFiltersChange({ ...filters, dateRange });
    },
    [filters, onFiltersChange]
  );

  // Handle dimensions change
  const handleDimensionsChange = useCallback(
    (dimensions: FilterWithLogic<ConductDimension> | undefined) => {
      onFiltersChange({ ...filters, dimensions });
    },
    [filters, onFiltersChange]
  );

  // Handle removing a single filter chip
  const handleRemoveFilter = useCallback(
    (type: ActiveFilterChip['type'], value?: string) => {
      const updated = { ...filters };

      switch (type) {
        case 'dateRange':
          delete updated.dateRange;
          break;
        case 'dimension':
          if (updated.dimensions?.values) {
            const filtered = updated.dimensions.values.filter((v) => v !== value);
            if (filtered.length === 0) {
              delete updated.dimensions;
            } else {
              updated.dimensions = { ...updated.dimensions, values: filtered };
            }
          }
          break;
        case 'search':
          delete updated.search;
          break;
        case 'conductScore':
          delete updated.conductScoreMin;
          delete updated.conductScoreMax;
          break;
      }

      onFiltersChange(updated);
    },
    [filters, onFiltersChange]
  );

  return (
    <div
      className={cn('space-y-4', className)}
      data-testid="filter-strip"
    >
      {/* Filter Controls Row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Date Range */}
        <DateRangeFilter
          value={filters.dateRange}
          onChange={handleDateRangeChange}
          disabled={isLoading}
          className="w-[180px]"
        />

        {/* Dimensions */}
        <DimensionFilter
          value={filters.dimensions}
          onChange={handleDimensionsChange}
          disabled={isLoading}
          showTcf={!!tcfAvailable}
          className="w-[200px]"
        />

        {/* Spacer */}
        <div className="flex-1" />

        {/* Reset Button */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={isLoading}
            className="rounded-full text-muted-foreground"
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      {/* Active Filter Chips */}
      <ActiveFilterChips
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
      />
    </div>
  );
}
